import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion } from "framer-motion";
import { Radar, TrendingUp, Crosshair, Mail } from "lucide-react";
import TypewriterText from "./components/TypewriterText";
import DecodingText from "./components/DecodingText";
import GlitchText from "./components/GlitchText";
import CountUpStats from "./components/CountUpStats";
import HolographicCard from "./components/HolographicCard";
import MarqueeSlider from "./components/MarqueeSlider";
import AudioPlayer from "./components/AudioPlayer";
import WhitepaperButton from './components/WhitepaperButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Audio Tracks Configuration
// To add more tracks, simply add objects to this array with name and url properties
const AUDIO_TRACKS = [
  {
    name: "BORN FROM THE HUSTLE",
    url: "https://cdn1.suno.ai/0054d8bb-3188-4fce-bc6a-19f45870fd05.mp3"
  },
  {
    name: "NEXT WAVE COLLECTIVE",
    url: "https://cdn1.suno.ai/8fd59e63-115f-4fe2-9ac1-05e8053e640e.mp3"
  },
  {
    name: "DEGEN ANTHEM",
    url: "https://cdn1.suno.ai/3a7656ff-b701-4c62-bf9e-d11d86c94b41.mp3"
  }
  // Add more tracks here as needed
];

function App() {
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [telegramCount, setTelegramCount] = useState(300);

  useEffect(() => {
    // Track page view
    trackAnalytics("page_view", { page: "landing" });
    
    // Fetch Telegram member count
    fetchTelegramCount();
  }, []);

  const fetchTelegramCount = async () => {

    try {
      const botToken = '8550182139:AAH6kRENFbitWaq_axj7-taTMiyP4sxUK8A'; 
      const chatId = '-1003640231730'; 
      const response = await fetch(`https://api.telegram.org/bot${botToken}/getChatMemberCount?chat_id=${chatId}`);
      const data = await response.json();

      if (data.ok) {
        setTelegramCount(data.result);
      }
    } catch (error) {
      console.error("Failed to query Telegram:", error);
    }
  };

  const trackAnalytics = async (eventType, eventData = {}) => {
    // Tracker neutralized. No more 404 errors.
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubscribeStatus("TRANSMITTING...");

    // Your specific Google Form connection
    const formUrl = 'https://docs.google.com/forms/d/e/1FAIpQLScBXVKOVCw1mUiQe8suj9Un1-_NN7EdA4cuXMPrJ7d9X0VkMQ/formResponse';
    
    // Package the email using your exact Entry ID
    const formData = new URLSearchParams();
    formData.append('entry.743181412', email);

    try {
      // Blast the data to Google silently
      await fetch(formUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });
      
      // Success update
      setSubscribeStatus("ACCESS GRANTED. SIGNAL RECEIVED.");
      setEmail(""); 
    } catch (error) {
      console.error(error);
      setSubscribeStatus("TRANSMISSION FAILED. CHECK CONNECTION.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleCTAClick = (ctaName) => {
    trackAnalytics("button_click", { button: ctaName });
  };

  return (
    <div className="App bg-background min-h-screen relative overflow-x-hidden">
      {/* Grid Background */}
      <div className="fixed inset-0 grid-bg opacity-50 pointer-events-none"></div>

      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 glass border-b border-border"
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_95379b36-8239-4b6d-9ea0-3b97102dbbb1/artifacts/m7dtegyb_IMG_20260119_213316_623.jpg"
              alt="NXT Wave Logo"
              className="h-12 w-12 object-contain"
            />
            <span className="font-space-mono text-2xl font-bold text-primary tracking-wider">
              NXT WAVE
            </span>
          </div>
          <a
            href="https://t.me/+cIASO2VKGr9lZWM5"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleCTAClick("nav_join_war_room")}
            data-testid="nav-join-war-room-btn"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-primary text-black font-space-mono font-bold px-6 py-3 uppercase tracking-widest hover:bg-white transition-colors duration-200"
            >
              <GlitchText>JOIN WAR ROOM</GlitchText>
            </motion.button>
          </a>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-24 pb-12 px-4 overflow-hidden" data-testid="hero-section">
        {/* Scan line effect */}
        <div className="scan-line"></div>
        
        {/* Background image with overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background z-10"></div>
          <img
            src="https://images.unsplash.com/photo-1746470427586-66ed7b83a502?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxjeWJlcnB1bmslMjBkaWdpdGFsJTIwZ3JpZCUyMHRleHR1cmV8ZW58MHx8fHwxNzcwNDM4NDIwfDA&ixlib=rb-4.1.0&q=85"
            alt="Digital Grid"
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className="relative z-20 max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-space-mono text-5xl sm:text-6xl lg:text-8xl font-bold text-white mb-6 leading-tight tracking-tighter uppercase">
              <TypewriterText text="THE TRENDING ENGINE OF SOLANA" delay={80} />
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3, duration: 0.8 }}
            className="font-inter text-lg sm:text-xl text-text-secondary max-w-4xl mx-auto mb-12 leading-relaxed"
            data-testid="hero-subheading"
          >
            NXT Wave is a collective of 300+ traders, builders, and whales
            dedicated to dominating the on-chain ecosystem.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 4, duration: 0.5 }}
          >
            <a
              href="https://t.me/+cIASO2VKGr9lZWM5"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleCTAClick("hero_join_war_room")}
              data-testid="hero-join-war-room-btn"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary text-black font-space-mono font-bold text-xl px-12 py-6 uppercase tracking-widest hover:bg-white hover:text-black transition-all duration-200 border-2 border-primary hover:border-white"
              >
                <GlitchText>JOIN THE WAR ROOM</GlitchText>
              </motion.button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-4 relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-space-mono text-4xl sm:text-5xl font-bold text-primary mb-12 uppercase tracking-wider"
          >
            &gt; <DecodingText text="WHO_WE_ARE" delay={200} />
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-surface border border-border p-8 sm:p-12 mb-16"
            data-testid="about-content"
          >
            <p className="font-inter text-lg text-text-secondary leading-relaxed mb-6">
              <span className="text-primary font-bold uppercase tracking-wider">
                STATUS: OPERATIONAL
              </span>
              <br />
              <span className="text-primary font-bold uppercase tracking-wider">
                TYPE: DECENTRALIZED INFRASTRUCTURE COLLECTIVE
              </span>
            </p>

            <p className="font-inter text-lg text-text-secondary leading-relaxed mb-6">
              NXT Wave represents the convergence of high-frequency trading
              infrastructure and decentralized social consensus. We are a
              meritocracy of 300+ active traders dedicated to dominating the
              Solana ecosystem.
            </p>

            <p className="font-inter text-lg text-text-secondary leading-relaxed mb-6">
              We reject the "paid group" model. Our value is proven on-chain.
              Through our proprietary NXT Trending Engine and Kinetic Volume
              Protocols, we force visibility and drive adoption for the projects
              we back.
            </p>

            <p className="font-inter text-lg text-primary font-bold leading-relaxed">
              We are the unfair advantage in a PvP market.
            </p>
          </motion.div>

          {/* Live Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="live-stats">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-surface border border-primary p-8 text-center hover:bg-primary/5 transition-colors duration-300"
              data-testid="stat-operators"
            >
              <div className="font-space-mono text-5xl font-bold text-primary mb-2">
                <CountUpStats end={telegramCount ? Math.floor(telegramCount / 100) * 100 : 300} />+
              </div>
              <div className="font-inter text-text-secondary uppercase tracking-widest text-sm">
                OPERATORS
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-surface border border-primary p-8 text-center hover:bg-primary/5 transition-colors duration-300"
              data-testid="stat-volume"
            >
              <div className="font-space-mono text-5xl font-bold text-primary mb-2">
                $<CountUpStats end={100} suffix="M+" />
              </div>
              <div className="font-inter text-text-secondary uppercase tracking-widest text-sm">
                VOLUME
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-surface border border-primary p-8 text-center hover:bg-primary/5 transition-colors duration-300"
              data-testid="stat-coverage"
            >
              <div className="font-space-mono text-5xl font-bold text-primary mb-2">
                24/7
              </div>
              <div className="font-inter text-text-secondary uppercase tracking-widest text-sm">
                COVERAGE
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-surface border border-primary p-8 text-center hover:bg-primary/5 transition-colors duration-300"
              data-testid="stat-telegram-members"
            >
              <div className="font-space-mono text-5xl font-bold text-primary mb-2">
                <CountUpStats end={telegramCount} />
              </div>
              <div className="font-inter text-text-secondary uppercase tracking-widest text-sm">
                TELEGRAM MEMBERS
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Arsenal Section */}
      <section className="py-24 px-4 relative" data-testid="arsenal-section">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-space-mono text-4xl sm:text-5xl font-bold text-primary mb-12 uppercase tracking-wider"
          >
            &gt; <DecodingText text="ARSENAL" delay={200} />
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <HolographicCard
              icon={Radar}
              title="NXT Trending Engine"
              description="Proprietary node infrastructure. 500ms faster than public aggregators."
              delay={0.1}
            />
            <HolographicCard
              icon={TrendingUp}
              title="Kinetic Volume"
              description="Organic market simulation. No wash-trading patterns."
              delay={0.2}
            />
            <HolographicCard
              icon={Crosshair}
              title="Raid Protocol"
              description="Social dominance on command."
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* Previous Ops Section */}
      <section className="py-24 px-4 relative" data-testid="previous-ops-section">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-space-mono text-4xl sm:text-5xl font-bold text-primary mb-12 uppercase tracking-wider"
          >
            &gt; <DecodingText text="PREVIOUS_OPS" delay={200} />
          </motion.h2>

          <MarqueeSlider />
        </div>
      </section>

      <WhitepaperButton />

  {/* Newsletter Section */}
      <section className="py-24 px-4 relative" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-surface border-2 border-primary p-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <Mail className="w-10 h-10 text-primary" />
              <h2 className="font-space-mono text-3xl sm:text-4xl font-bold text-white uppercase tracking-wider">
                INTEL BRIEFING
              </h2>
            </div>

            <p className="font-inter text-text-secondary mb-8 leading-relaxed">
              Subscribe for exclusive alpha, operation updates, and strategic
              insights delivered directly to your terminal.
            </p>

            <form onSubmit={handleNewsletterSubmit} className="space-y-4" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ENTER EMAIL ADDRESS"
                required
                className="w-full bg-black border border-border focus:border-primary text-white placeholder-text-muted p-4 font-space-mono uppercase tracking-wider outline-none transition-colors"
                data-testid="newsletter-email-input"
              />

              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-primary text-black font-space-mono font-bold py-4 uppercase tracking-widest hover:bg-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="newsletter-submit-btn"
              >
                {isLoading ? "TRANSMITTING..." : "SUBSCRIBE"}
              </motion.button>
            </form>

            {subscribeStatus && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`mt-4 font-space-mono text-sm uppercase tracking-wider ${
                  subscribeStatus.includes("ERROR")
                    ? "text-red-500"
                    : "text-primary"
                }`}
                data-testid="newsletter-status-message"
              >
                {subscribeStatus}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-border" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="font-space-mono text-text-secondary text-sm uppercase tracking-wider">
              © 2026 NXT Wave Collective. All Systems Operational.
            </div>

            <div className="flex items-center gap-6">
              <a
                href="https://t.me/+cIASO2VKGr9lZWM5"
                target="_blank"
                rel="noopener noreferrer"
                className="font-space-mono text-text-secondary hover:text-primary transition-colors uppercase tracking-wider text-sm"
                data-testid="footer-telegram-link"
              >
                TELEGRAM
              </a>
              <a
                href="https://x.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-space-mono text-text-secondary hover:text-primary transition-colors uppercase tracking-wider text-sm"
                data-testid="footer-x-link"
              >
                X
              </a>
              <a
                href="https://gitbook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-space-mono text-text-secondary hover:text-primary transition-colors uppercase tracking-wider text-sm"
                data-testid="footer-gitbook-link"
              >
                GITBOOK
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Audio Player */}
      <AudioPlayer tracks={AUDIO_TRACKS} />
    </div>
  );
}

export default App;
